import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import { FollowUpTimingEnum } from "../../../enums/FollowUpTimingEnum";
import { FollowUpRecipientsEnum } from "../../../enums/FollowUpRecipientsEnum";
import { v4 as uuidv4 } from "uuid";


const TimingConfiguration = ({ title, channelId, followUpSettings, onTimingChange, showDefaultSchedule = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [adminTimings, setAdminTimings] = useState([]);
  const [leadTimings, setLeadTimings] = useState([]);



    // MAP API → UI
  const mapApiToUi = timings =>
    timings.map((t, i) => ({
      id: t.id,
      value: t.timeValue,
      offsetDescription: t.offsetDescription,
      unit:
        t.timeUnit === FollowUpTimingEnum.Minutes
          ? "mins"
          : t.timeUnit === FollowUpTimingEnum.Hours
          ? "hours"
          : "days"
    }));

   const mapUiToApi = (timings = []) =>
  timings.map(t => ({
    id : t.id,
    timeValue: t.value,
    timeUnit:
      t.unit === "mins"
        ? FollowUpTimingEnum.Minutes
        : t.unit === "hours"
        ? FollowUpTimingEnum.Hours
        : FollowUpTimingEnum.Days,
    offsetDescription:
      t.value === 0
        ? "When webinar starts"
        : `${t.value} ${t.unit} before webinar`
  }));

  useEffect(() => {
    if (!followUpSettings?.length) return;

    const admin = followUpSettings.find(
      x => x.recipientId === FollowUpRecipientsEnum.AdminNotification
    );

    const lead = followUpSettings.find(
      x => x.recipientId === FollowUpRecipientsEnum.LeadCommunication
    );

    setAdminTimings(mapApiToUi(admin?.followUpTimings || []));
    setLeadTimings(mapApiToUi(lead?.followUpTimings || []));
  }, [followUpSettings]);

  const updateFollowUpSettings = (recipientId, updatedUiTimings) => {
    const updatedApiTimings = mapUiToApi(updatedUiTimings);

    if (onTimingChange) {
      onTimingChange(recipientId, channelId, updatedApiTimings);
    }
  };


  const addTiming = (timings, setTimings, recipientId) => {
    const updated = [
      ...timings,
      { id: uuidv4(), value: 1, unit: "hours" }
    ];

    setTimings(updated);
    updateFollowUpSettings(recipientId, updated);
  };

  const updateTiming = (timings, setTimings, recipientId, id, field, value) => {
    const updated = timings.map(t =>
      t.id === id
        ? { ...t, [field]: field === "value" ? +value : value }
        : t
    );

    setTimings(updated);
    updateFollowUpSettings(recipientId, updated);
  };

  const deleteTiming = (timings, setTimings, recipientId, id) => {
    const updated = timings.filter(t => t.id !== id);

    setTimings(updated);
    updateFollowUpSettings(recipientId, updated);
  };

  const formatTimingDisplay = useCallback((timing) => {
    if (timing.value === 0) return 'When webinar starts';
    return `${timing.value} ${timing.unit} before webinar`;
  }, []);

  const TimingList = ({ timings, setTimings, recipientId, type }) => {
    const isMaxReached = timings.length >= 5;
    
    return (
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-clock me-2"></i>
            <h6 className="mb-0 fw-bold">{type} Timing</h6>
          </div>
          <div className="position-relative">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => addTiming(timings, setTimings, recipientId)}
              disabled={isMaxReached}
              style={{ 
                fontSize: '12px', 
                padding: '4px 8px',
                opacity: isMaxReached ? 0.6 : 1,
                cursor: isMaxReached ? 'not-allowed' : 'pointer'
              }}
              title={isMaxReached ? "You can only have five configurable timings" : "Add new timing"}
            >
              <i className="bi bi-plus me-1"></i>
              Add
            </Button>
            {isMaxReached && (
              <div 
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ cursor: 'not-allowed' }}
                title="You can only have five configurable timings"
              />
            )}
          </div>
        </div>
        
        <div className="mb-3">
          {timings.map((timing) => (
            <div key={timing.id} className="mb-3">
              <div className="d-flex align-items-center gap-2 p-3 bg-light rounded">
                <Form.Select
                  value={timing.value}
                  onChange={(e) => {
                    updateTiming(timings, setTimings, recipientId, timing.id, 'value', e.target.value);
                  }}
                  style={{ width: '80px' }}
                  size="sm"
                >
                  {[...Array(61)].map((_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </Form.Select>
                
                <Form.Select
                  value={timing.unit}
                  onChange={(e) => {
                    updateTiming(timings, setTimings, recipientId, timing.id, 'unit', e.target.value);
                  }}
                  style={{ width: '100px' }}
                  size="sm"
                >
                  <option value="mins">mins</option>
                  <option value="hours">hours</option>
                  <option value="days">days</option>
                </Form.Select>
                
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => deleteTiming(timings, setTimings, recipientId, timing.id)}
                  className="ms-auto"
                  style={{ fontSize: '12px', padding: '4px 8px', minWidth: '32px' }}
                  title={`Delete ${formatTimingDisplay(timing)}`}
                >
                  <i className="bi bi-trash text-danger"></i>
                </Button>
              </div>
              
              <div className="small text-muted mt-1 ps-3">
                {timing.offsetDescription}
              </div>
            </div>
          ))}
        </div>
        
        {isMaxReached && (
          <div className="small text-muted text-center mt-2">
            <i className="bi bi-info-circle me-1"></i>
            Maximum of 5 timing configurations reached
          </div>
        )}
      </div>
    );
  };

  const DefaultScheduleDisplay = () => (
    <div className="mb-4">
      <h6 className="fw-bold mb-3 text-muted">Default Schedule:</h6>
      <div className="small text-muted">
        <div className="mb-1">• 2 days before webinar</div>
        <div className="mb-1">• 2 hours before webinar</div>
        <div className="mb-1">• 15 minutes before webinar</div>
        <div className="mb-1">• When webinar starts</div>
      </div>
    </div>
  );

  return (
    <Card className="border-0 bg-light">
      <Card.Body className="p-3">
        <div 
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h6 className="mb-0 fw-bold">{title}</h6>
          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
        </div>
        
        {isExpanded && (
          <div className="mt-4">
            <div className="row">
              <div className={showDefaultSchedule ? "col-md-8" : "col-12"}>
                <TimingList 
                  timings={adminTimings} 
                  setTimings={setAdminTimings} 
                  recipientId={FollowUpRecipientsEnum.AdminNotification}
                  type="Admin" 
                />
                <TimingList 
                  timings={leadTimings} 
                  setTimings={setLeadTimings} 
                  recipientId={FollowUpRecipientsEnum.LeadCommunication}
                  type="Lead" 
                />
              </div>
              {showDefaultSchedule && (
                <div className="col-md-4">
                  <DefaultScheduleDisplay />
                </div>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TimingConfiguration; 